/**
 * Realtime **patching** of the item card's update list (`specs/M1-B7` §9, round 5o).
 *
 * Round 5 shipped the events but not the reducer: the card reloaded its whole list after its
 * own write, and another person's update, reply or like never appeared at all until the card
 * was closed and reopened. This is the reducer, and `useItemCardRealtime` is the subscription
 * that feeds it.
 *
 * **One event type carries four things.** `services/updates.ts` emits `update.created` for a
 * new update, for a reply (`parentId`), for an **edit** (`edited: true`) and for a **like**
 * (`liked: true, likes, userId`) — plus `update.deleted` for a removal. Nothing new had to be
 * emitted for this round; the payloads were already there.
 *
 * What the reducer cannot do on its own: a brand-new update from **another** user arrives with
 * `createdBy` as an id, and the card needs a `CardPerson` to render the avatar and the name.
 * `resolvePerson` is handed in by the caller (the card knows the board's members); when it
 * cannot resolve the id, the update still renders with a null creator rather than being
 * dropped — which is what monday does for a member who has since left.
 */
import type { EventEnvelope } from '@elizmonday/shared';
import type { CardPerson, CardUpdate } from './types';

/** The half of `update.created`'s payload this reducer reads. */
interface UpdatePayload {
  updateId?: unknown;
  parentId?: unknown;
  bodyHtml?: unknown;
  bodyText?: unknown;
  createdBy?: unknown;
  edited?: unknown;
  liked?: unknown;
  likes?: unknown;
  userId?: unknown;
}

const str = (value: unknown): string | null =>
  typeof value === 'string' ? value : typeof value === 'number' ? String(value) : null;

/** Depth-first map over an update and its replies. */
function mapTree(list: readonly CardUpdate[], fn: (entry: CardUpdate) => CardUpdate): CardUpdate[] {
  return list.map((entry) => fn({ ...entry, replies: mapTree(entry.replies, fn) }));
}

function removeFromTree(list: readonly CardUpdate[], id: string): CardUpdate[] {
  return list
    .filter((entry) => entry.id !== id)
    .map((entry) => ({ ...entry, replies: removeFromTree(entry.replies, id) }));
}

function existsInTree(list: readonly CardUpdate[], id: string): boolean {
  // `entry.replies` defensively: one malformed row used to take the whole screen down with
  // it, and a tree walk is the wrong place to discover a missing field (round 6g).
  return list.some((entry) => entry.id === id || existsInTree(entry.replies ?? [], id));
}

export interface ItemCardPatchContext {
  /** The viewer, so a like by *me* toggles my own state and not somebody else's. */
  readonly me: CardPerson | null;
  readonly itemId: string;
  resolvePerson(userId: string): CardPerson | null;
}

/**
 * Applies one envelope to the update list and returns the next list — or the **same array
 * reference** when the event is not for this card, so React skips the re-render.
 */
export function applyUpdateEvent(
  updates: readonly CardUpdate[],
  event: EventEnvelope,
  context: ItemCardPatchContext,
): readonly CardUpdate[] {
  if (event.itemId !== undefined && event.itemId !== null && String(event.itemId) !== context.itemId) {
    return updates;
  }
  const payload = (event.payload ?? {}) as UpdatePayload;
  const updateId = str(payload.updateId);
  if (!updateId) return updates;

  if (event.type === 'update.deleted') {
    if (!existsInTree(updates, updateId)) return updates;
    return removeFromTree(updates, updateId);
  }
  if (event.type !== 'update.created') return updates;

  /* A like or an unlike: only the counter and my own state change. */
  if (payload.liked === true || payload.liked === false) {
    if (!existsInTree(updates, updateId)) return updates;
    const likes = typeof payload.likes === 'number' ? payload.likes : null;
    const actor = str(payload.userId);
    return mapTree(updates, (entry) => {
      if (entry.id !== updateId) return entry;
      const mine = actor !== null && context.me !== null && actor === String(context.me.id);
      let next = entry.likes;
      if (mine) {
        next = payload.liked
          ? [
              ...entry.likes.filter((like) => String(like.userId) !== actor),
              { userId: actor, reactionType: 'like', createdAt: new Date().toISOString() },
            ]
          : entry.likes.filter((like) => String(like.userId) !== actor);
      } else if (likes !== null && likes !== entry.likes.length) {
        // Somebody else's like: keep the rows we know and pad the count to the server's.
        const padding = Array.from({ length: Math.max(0, likes - entry.likes.length) }, (_, index) => ({
          userId: `other-${String(index)}`,
          reactionType: 'like',
          createdAt: new Date().toISOString(),
        }));
        next = likes < entry.likes.length ? entry.likes.slice(0, likes) : [...entry.likes, ...padding];
      }
      return { ...entry, likes: next };
    });
  }

  /* An edit: the body is replaced in place and the row is marked edited. */
  if (payload.edited === true) {
    if (!existsInTree(updates, updateId)) return updates;
    const bodyHtml = str(payload.bodyHtml);
    const bodyText = str(payload.bodyText);
    const at = event.entityUpdatedAt ?? event.createdAt ?? new Date().toISOString();
    return mapTree(updates, (entry) =>
      entry.id === updateId
        ? { ...entry, bodyHtml: bodyHtml ?? entry.bodyHtml, bodyText: bodyText ?? entry.bodyText, editedAt: at, updatedAt: at }
        : entry,
    );
  }

  /* A new update or reply. Our own writes already inserted it, so this is idempotent. */
  if (existsInTree(updates, updateId)) return updates;
  const createdAt = event.entityUpdatedAt ?? event.createdAt ?? new Date().toISOString();
  const creatorId = str(payload.createdBy);
  const fresh: CardUpdate = {
    id: updateId,
    itemId: context.itemId,
    parentId: str(payload.parentId),
    bodyHtml: str(payload.bodyHtml) ?? '',
    bodyText: str(payload.bodyText) ?? '',
    createdAt,
    updatedAt: createdAt,
    editedAt: null,
    pinned: false,
    pinnedAt: null,
    /*
     * P-303: the board's people first, then the viewer. An event for **my own** post can
     * arrive before the members list has ever been fetched (a card opened straight from a
     * deep link), and a row with no creator prints "? Unknown".
     */
    creator: creatorId === null ? null : (context.resolvePerson(creatorId) ?? meIf(context.me, creatorId)),
    mentions: [],
    likes: [],
    assets: [],
    replies: [],
  };
  if (fresh.parentId === null) {
    // monday puts the newest update at the top of the list, under the pinned ones.
    return [fresh, ...updates];
  }
  const parentId = fresh.parentId;
  if (!existsInTree(updates, parentId)) return updates;
  return mapTree(updates, (entry) =>
    entry.id === parentId ? { ...entry, replies: [...entry.replies, fresh] } : entry,
  );
}

/* ── local patches, for our own writes ────────────────────────────────────── */

/** The viewer, when they are the person the id names — P-303's fallback, and nobody else's. */
function meIf(me: CardPerson | null, userId: string | null): CardPerson | null {
  return me !== null && userId !== null && String(me.id) === userId ? me : null;
}

/**
 * **P-303 — "? Unknown · now" on the update I have just written.**
 *
 * A write's answer is inserted into the card's tree as it stands, and an answer with no
 * `creator` (an older api, a realtime payload that only carries `createdBy`) printed
 * `creator?.name ?? 'Unknown'` until the card was reloaded. The author of our own write is,
 * by definition, the viewer — so a creator-less row is stamped with `me` on the way in. A row
 * that **has** a creator is never touched: another person's update keeps their name.
 */
function withCreator(update: CardUpdate, me: CardPerson | null): CardUpdate {
  if (update.creator ?? null) return update;
  return me === null ? update : { ...update, creator: me };
}

/**
 * Our own write does not wait for its event: the mutation **returns the row**, so the card
 * patches immediately and the event that follows is a no-op (the reducer above is idempotent
 * on `updateId`). This is also what keeps the card correct where there is no socket at all —
 * the `/__ui/board` fixture and the unit tests.
 */
export function insertUpdate(
  updates: readonly CardUpdate[],
  created: CardUpdate,
  me: CardPerson | null = null,
): CardUpdate[] {
  const row = withCreator(created, me);
  if (existsInTree(updates, row.id)) return replaceUpdate(updates, row, me);
  if (row.parentId === null) return [row, ...updates];
  if (!existsInTree(updates, row.parentId)) return [...updates];
  return mapTree(updates, (entry) =>
    entry.id === row.parentId ? { ...entry, replies: [...entry.replies, row] } : entry,
  );
}

/** An edit: the server's row wins, but the replies we already hold are kept. */
export function replaceUpdate(
  updates: readonly CardUpdate[],
  next: CardUpdate,
  me: CardPerson | null = null,
): CardUpdate[] {
  const row = withCreator(next, me);
  return mapTree(updates, (entry) =>
    entry.id === row.id
      ? { ...row, replies: row.replies.length > 0 ? row.replies : entry.replies }
      : entry,
  );
}

export function removeUpdate(updates: readonly CardUpdate[], updateId: string): CardUpdate[] {
  return removeFromTree(updates, updateId);
}

export function toggleLikeLocally(
  updates: readonly CardUpdate[],
  updateId: string,
  liked: boolean,
  me: CardPerson | null,
): CardUpdate[] {
  if (!me) return [...updates];
  const mine = String(me.id);
  return mapTree(updates, (entry) =>
    entry.id === updateId
      ? {
          ...entry,
          likes: liked
            ? [
                ...entry.likes.filter((like) => String(like.userId) !== mine),
                { userId: mine, reactionType: 'like', createdAt: new Date().toISOString() },
              ]
            : entry.likes.filter((like) => String(like.userId) !== mine),
        }
      : entry,
  );
}

export function setPinnedLocally(
  updates: readonly CardUpdate[],
  updateId: string,
  pinned: boolean,
): CardUpdate[] {
  return mapTree(updates, (entry) =>
    entry.id === updateId
      ? { ...entry, pinned, pinnedAt: pinned ? new Date().toISOString() : null }
      : entry,
  );
}
